import { Fragment, useContext, useState } from 'react';
import { Case } from '../../../api/case';
import Table, { HeaderComponent } from '../../../components/table/Table';
import { formatDateTime } from '../../../utils/date-utils';
import Modal from '../../../components/modal/Modal';
import CaseStep from '../case-step/CaseStep';
import { AppContext } from '../../../App';
import * as api from '../../../api';

interface Props {
  _case: Case;
  upstreamSelectedStepId?: string;
  setUpstreamSelectedStepId?: (stepId?: string) => void;
  refreshCase?: () => Promise<void>;
}

const CaseStepHistory: React.FC<Props> = ({
  _case,
  upstreamSelectedStepId,
  setUpstreamSelectedStepId,
  refreshCase,
}) => {
  const ctx = useContext(AppContext);
  const [selectedStepId, setSelectedStepId] = useState<string | undefined>();
  const [moveBackStepId, setMoveBackStepId] = useState<string | undefined>();
  const [isMovingBack, setIsMovingBack] = useState(false);

  const handleMoveBack = async (processStepId: string) => {
    setIsMovingBack(true);
    const res = await api.cases.moveToStep(_case.id, processStepId);
    if (res.data) {
      ctx.setToast({ message: 'Case moved back to step successfully', type: 'success', clearAfterMs: 3000 });
      setMoveBackStepId(undefined);
      if (refreshCase) {
        await refreshCase();
      }
    }
    setIsMovingBack(false);
  };

  const completedSteps = _case.caseSteps
    .filter((step) => step.completedAt !== null)
    .sort((a, b) => {
      return new Date(a.completedAt!).getTime() - new Date(b.completedAt!).getTime();
    })
    .map((caseStep) => {
      const processStep = _case.process.steps.find((step) => step.id === caseStep.processStepId);
      return {
        id: caseStep.id,
        processStepId: caseStep.processStepId,
        stepName: processStep?.taxonomyLabel ? processStep?.taxonomyLabel + ' ' + processStep?.name: processStep?.name,
        completedAt: caseStep.completedAt
          ? formatDateTime(caseStep.completedAt, ctx.user.company.locale, ctx.user.company.timezone)
          : '',
        decision: caseStep.decision,
      };
    });

  const idHeader: HeaderComponent<(typeof completedSteps)[0]> = (step) => {
    return {
      header: 'id',
      element: (
        <button className="link-button" onClick={() => setSelectedStepId(step?.id)}>
          {step?.id.slice(0, 6)}
        </button>
      ),
    };
  };

  const actionsHeader: HeaderComponent<(typeof completedSteps)[0]> = (step) => {
    const canMoveBack = _case.process.allowMoveBackSteps === true;
    return {
      header: 'Actions',
      element: (
        <div>
          {canMoveBack && (
            <button
              className="link-button"
              onClick={() => setMoveBackStepId(step?.processStepId)}
              disabled={isMovingBack}
            >
              Move back to this step
            </button>
          )}
        </div>
      ),
    };
  };

  return (
    <Fragment>
      <Table
        data={completedSteps}
        search={true}
        headers={[idHeader, 'stepName', 'completedAt', actionsHeader]}
      />
      <Modal
        isOpen={!!selectedStepId || !!upstreamSelectedStepId}
        handleClose={() => {
          setSelectedStepId(undefined);
          if (setUpstreamSelectedStepId) {
            setUpstreamSelectedStepId(undefined);
          }
        }}
        modalStyles={{ maxWidth: '1000px' }}
      >
        {(!!selectedStepId || !!upstreamSelectedStepId) && (
          <CaseStep
            _case={_case}
            caseStepId={selectedStepId || upstreamSelectedStepId || ''}
            updateCase={() => {}}
          />
        )}
      </Modal>
      <Modal
        isOpen={!!moveBackStepId}
        handleClose={() => setMoveBackStepId(undefined)}
        modalStyles={{ maxWidth: '500px' }}
      >
        {!!moveBackStepId && (
          <div>
            <h3>Move case back to step?</h3>
            <p>
              This will move the case back to the selected step. All steps completed after this step will be reset.
            </p>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                className="button"
                onClick={() => moveBackStepId && handleMoveBack(moveBackStepId)}
                disabled={isMovingBack}
              >
                {isMovingBack ? 'Moving...' : 'Confirm'}
              </button>
              <button
                className="button button--secondary"
                onClick={() => setMoveBackStepId(undefined)}
                disabled={isMovingBack}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Fragment>
  );
};

export default CaseStepHistory;
